import SearchInput from "../../elements/search/SearchInput";
import { List, Checkbox, Spin, Tooltip, Typography, Slider } from "antd";
import "./userlistcomponent.css";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Initials from "../../../../components/generic/generateinitials/Initials";
import { isEqual } from "lodash";
import InfoCardComponent from "../../../../components/generic/infocard/InfoCardComponent";
import apiService from "../../../../service/apiservice/apiService";
import { getFullName } from "../../../careprovider/visits/utils";
import { MultiSelect } from "../../elements/multiselect/MultiSelect";
import CustomModal from "../../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../../components/generic/buttons/SecondaryButton";
import { mapWorkforceToProfileData } from "../../../../components/generic/profiledataextractor/profileDataExtractor";
import { useRecoilValue } from "recoil";
import { officeLabel } from "../../../../components/recoil/officeLabelRecoil";
import { currentConfiguration } from "../../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../../utils/configurationconstants";
import IntentType from "../../../../intentexecutor/utils/IntentType";
import { executeIntent } from "../../../utils/IntentExecutor";
import { useGetOrgIdentifier } from "../../hooks/useGetOrgIdentifier";
import ProfileOverview from "../../../../components/generic/profileoverview/ProfileOverview";
import { getMediaKeyObjectStore } from "../../../../components/generic/formbuilder/networkservice/FormService";

const FILTER_OPTIONS = [
  {
    label: "Services",
    value: "Services",
  },
  {
    label: "Distance",
    value: "Distance",
  },
  {
    label: "Preferences",
    value: "Preferences",
  },
];

const SUGGESTIONS_ENDPOINT =
  "niramayafoundation/niramayaworkforce/api/v1/suggestions";

const VolunteerSuggestionRow = memo(function VolunteerSuggestionRow({
  item,
  checked,
  onSelectWorkforce,
  showDetails,
  onAvatarClick,
}) {
  const workforce = item?.workforce;
  const fullName = getFullName(workforce?.givenName, workforce?.familyName);

  const handleCheckboxChange = useCallback(() => {
    onSelectWorkforce(workforce);
  }, [onSelectWorkforce, workforce]);

  const handleAvatarClick = useCallback(() => {
    onAvatarClick(workforce);
  }, [onAvatarClick, workforce]);

  const description = (
    <>
      {item?.justificationString && (
        <Tooltip title={item.justificationString}>
          <Typography.Text ellipsis>{item.justificationString}</Typography.Text>
        </Tooltip>
      )}
      {item?.negativePreference && (
        <Tooltip title={item.negativePreference}>
          <Typography.Text style={{ marginTop: 2 }} ellipsis>
            {item.negativePreference}
          </Typography.Text>
        </Tooltip>
      )}
    </>
  );

  return (
    <List.Item
      className="list-item-wrapper"
      actions={[
        <Checkbox
          key="select"
          className="round-checkbox"
          checked={checked}
          onChange={handleCheckboxChange}
        />,
      ]}
    >
      <InfoCardComponent
        avatar={
          <div
            className={showDetails ? "cursor-pointer" : undefined}
            onClick={showDetails ? handleAvatarClick : undefined}
            role={showDetails ? "button" : undefined}
          >
            <Initials value={fullName} />
          </div>
        }
        title={fullName}
        description={description}
      />
    </List.Item>
  );
});

const UserListComponent = ({
  onDataChange,
  payloadDataForWorkforceEntityRecommender,
  filterRequired,
  showDistanceSlider,
  showSelectAll,
  isMultiSelect,
  showDetails,
}) => {
  const organizationIdentifier = useGetOrgIdentifier();
  const label = useRecoilValue(officeLabel);
  const configuration = useRecoilValue(currentConfiguration);
  const bucketName =
    configuration[ConfigurationConstants.NIRAMAYA_FORMS_BUCKETNAME];
  const [userData, setUserData] = useState([]);
  const fetchTokenRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [payloadData, setPayloadData] = useState(
    payloadDataForWorkforceEntityRecommender,
  );
  const [distanceValue, setDistanceValue] = useState(0);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [volunteerDetails, setVolunteerDetails] = useState(null);
  const [isVolunteerDetailsLoading, setIsVolunteerDetailsLoading] =
    useState(false);
  const [volunteerPreferenceOptions, setVolunteerPreferenceOptions] =
    useState(null);

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  const selectedIdSet = useMemo(() => new Set(selectedItems), [selectedItems]);

  const handleSelectAll = useCallback(() => {
    if (!areAllSelected) {
      setSelectedItems(userData.map((row) => row?.workforce?.userIdentifier));
      setSelectedUsers(userData.map((row) => row?.workforce));
    } else {
      setSelectedItems([]);
      setSelectedUsers([]);
    }
    setAreAllSelected((prev) => !prev);
  }, [areAllSelected, userData]);

  const handleSelectItem = useCallback(
    (item) => {
      const id = item?.userIdentifier;
      const isSelected = selectedItems.includes(id);

      let newSelectedUsers;
      if (isSelected) {
        newSelectedUsers = selectedUsers.filter(
          (user) => user.userIdentifier !== id,
        );
        setAreAllSelected(false);
      } else if (isMultiSelect === true) {
        newSelectedUsers = [...selectedUsers, item];
      } else {
        newSelectedUsers = [item];
      }

      setSelectedItems(
        newSelectedUsers.map((selected) => selected.userIdentifier),
      );
      setSelectedUsers(newSelectedUsers);
    },
    [isMultiSelect, selectedItems, selectedUsers],
  );

  const fetchWorkforceListData = useCallback(async () => {
    const token = ++fetchTokenRef.current;
    setLoading(true);
    const payload = {
      ...payloadData,
      ...(distanceValue && { requiredWithInRadius: distanceValue }),
    };
    const response = await apiService.post(SUGGESTIONS_ENDPOINT, payload);
    if (token === fetchTokenRef.current) {
      setUserData(response ?? []);
    }
    setLoading(false);
  }, [payloadData, distanceValue]);

  const fetchSearchedData = useCallback(async () => {
    const token = ++fetchTokenRef.current;
    const searchPayload = { ...payloadData, searchQuery };
    const response = await apiService.post(SUGGESTIONS_ENDPOINT, searchPayload);
    if (token === fetchTokenRef.current) {
      setUserData(response ?? []);
    }
  }, [payloadData, searchQuery]);

  const handleFilterSelection = useCallback((value) => {
    setSelectedFilters(value);
  }, []);

  const handleInitialsClick = useCallback(
    async (workforce) => {
      if (!showDetails) return;

      setShowDetailsModal(true);
      setVolunteerDetails(null);
      setIsVolunteerDetailsLoading(true);

      try {
        let workforceWithDetails = workforce;

        if (workforce?.userIdentifier) {
          const userData = await executeIntent(
            IntentType.GET_WORKFORCE_DETAILS_BY_USER_IDENTIFIER,
            { userIdentifier: workforce.userIdentifier },
          );
          workforceWithDetails = userData?.[0] ?? workforce;
        }

        if (workforceWithDetails?.profilePicture && bucketName?.value) {
          const icon = await getMediaKeyObjectStore(
            workforceWithDetails.profilePicture,
            bucketName.value,
          );
          if (icon) {
            workforceWithDetails = { ...workforceWithDetails, icon };
          }
        }

        const volunteer = mapWorkforceToProfileData(
          workforceWithDetails,
          label,
          false,
          false,
          volunteerPreferenceOptions,
        );
        setVolunteerDetails(volunteer);
      } catch {
        const volunteer = mapWorkforceToProfileData(
          workforce,
          label,
          false,
          false,
          volunteerPreferenceOptions,
        );
        setVolunteerDetails(volunteer);
      } finally {
        setIsVolunteerDetailsLoading(false);
      }
    },
    [bucketName?.value, label, showDetails, volunteerPreferenceOptions],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setVolunteerDetails(null);
    setIsVolunteerDetailsLoading(false);
    setShowDetailsModal(false);
  }, []);

  const distanceSliderFormatter = useCallback(
    (value) => (value > 1 ? `${value} miles` : `${value} mile`),
    [],
  );

  useEffect(() => {
    if (!isEqual(payloadData, payloadDataForWorkforceEntityRecommender)) {
      setPayloadData(payloadDataForWorkforceEntityRecommender);
    }
  }, [payloadData, payloadDataForWorkforceEntityRecommender]);

  useEffect(() => {
    if (searchQuery.length > 0) {
      fetchSearchedData();
    } else {
      fetchWorkforceListData();
    }
  }, [
    searchQuery,
    payloadData,
    distanceValue,
    fetchSearchedData,
    fetchWorkforceListData,
  ]);

  useEffect(() => {
    onDataChangeRef.current(selectedUsers, [], areAllSelected, selectedFilters, distanceValue);
  }, [areAllSelected, selectedUsers, selectedFilters, distanceValue]);

  useEffect(() => {
    setPayloadData((payload) => ({ ...payload }));
  }, [selectedFilters, payloadDataForWorkforceEntityRecommender]);

  useEffect(() => {
    const getPreferenceOptions = async () => {
      const payload = {
        configurationKey: ["niramaya.volunteer.preference.options"],
      };

      const result = await executeIntent(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier,
        },
      );
      const options =
        result[0].configurationValue &&
        JSON.parse(result[0].configurationValue);

      setVolunteerPreferenceOptions(options);
    };

    getPreferenceOptions();
  }, [organizationIdentifier]);

  const renderListItem = useCallback(
    (item) => (
      <VolunteerSuggestionRow
        key={item?.workforce?.userIdentifier}
        item={item}
        checked={selectedIdSet.has(item?.workforce?.userIdentifier)}
        onSelectWorkforce={handleSelectItem}
        showDetails={showDetails}
        onAvatarClick={handleInitialsClick}
      />
    ),
    [handleInitialsClick, handleSelectItem, selectedIdSet, showDetails],
  );

  return (
    <div className="listofusers-main-section-items">
      <div className="search-wrapper">
        <SearchInput
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          cssClass="searchContainer"
          searchPlaceholder={"Search volunteer... "}
        />
      </div>
      <div className="workforce-list-wrapper">
        <div className="header-and-select-all-checkbox">
          <div className="header-section">
            Volunteer Suggestions
            <div className="header-section">
              {filterRequired && (
                <MultiSelect
                  options={FILTER_OPTIONS}
                  value={selectedFilters}
                  onChange={handleFilterSelection}
                  placeholder="Filter Volunteers By"
                  isClearable={true}
                  allLabel="All"
                />
              )}
            </div>
          </div>
          <div className="action-section">
            <div className="d-flex flex-column" style={{ gap: 4 }}>
              {showDistanceSlider && (
                <div>
                  <span className="header-section">
                    Volunteer Distance (Miles)
                  </span>
                  <Slider
                    onChange={setDistanceValue}
                    value={distanceValue}
                    max={50}
                    min={0}
                    tooltip={{ formatter: distanceSliderFormatter }}
                  />
                </div>
              )}
              {showSelectAll && (
                <label className="select-all-checkbox-wrapper">
                  <Checkbox
                    type="checkbox"
                    className="round-checkbox"
                    checked={areAllSelected}
                    onChange={handleSelectAll}
                  />
                  <span>Select All</span>
                </label>
              )}
            </div>
          </div>
        </div>
        <div className="user-list-wrapper">
            <List
              className="user-list"
              itemLayout="horizontal"
              dataSource={userData}
              renderItem={renderListItem}
              loading={isLoading}
            />
        </div>
      </div>

      {showDetailsModal && (
        <CustomModal
          className="invoice-details-modal"
          isModalOpen={showDetailsModal}
          closeModal={setShowDetailsModal}
          footer={
            <SecondaryButton
              buttonText="Close"
              handleOnClick={handleCloseDetailsModal}
            />
          }
          title="Volunteer Details"
          closable
        >
          {isVolunteerDetailsLoading || !volunteerDetails?.overviewConfig ? (
            <Spin className="d-flex justify-content-center m-auto py-5" />
          ) : (
            <ProfileOverview overviewConfig={volunteerDetails.overviewConfig} />
          )}
        </CustomModal>
      )}
    </div>
  );
};

export default UserListComponent;
